/**
 * Refueling Simulation View — Main orchestrator component
 *
 * Manages simulation state, playback controls, Zulu time display,
 * time scrubbing, and mission abort state.
 */

import { useState, useRef, useCallback } from 'react'
import type { AARScenarioConfig, AARSimState, AARContingency } from '../../../../types'
import {
  initSimState,
  tickSimulation,
  applyContingency,
  formatSimTime,
  formatZuluTime,
} from '../../../../services/aarEngine'
import { ScenarioSetup } from './ScenarioSetup'
import { GanttChart } from './GanttChart'
import { TankerDashboard } from './TankerDashboard'
import { VoiceScript } from './VoiceScript'
import { ContingencyPanel } from './ContingencyPanel'

const SPEED_OPTIONS = [1, 2, 5, 10] as const

export function RefuelingSimView() {
  const [simState, setSimState] = useState<AARSimState | null>(null)
  const [scenarioConfig, setScenarioConfig] = useState<AARScenarioConfig | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const stateRef = useRef<AARSimState | null>(null)
  const configRef = useRef<AARScenarioConfig | null>(null)

  // Keep stateRef in sync for interval callback
  const updateState = useCallback((newState: AARSimState) => {
    stateRef.current = newState
    setSimState(newState)
  }, [])

  const clearTick = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
  }, [])

  const startTick = useCallback((speed: 1 | 2 | 5 | 10) => {
    clearTick()
    tickRef.current = setInterval(() => {
      const current = stateRef.current
      if (!current || current.phase !== 'RUNNING') {
        clearTick()
        return
      }
      const next = tickSimulation(current)
      updateState(next)
      // Auto-pause on mission abort
      if (next.missionAborted && !current.missionAborted) {
        clearTick()
        updateState({ ...next, phase: 'PAUSED', isRunning: false })
        return
      }
      if (next.phase === 'COMPLETE') {
        clearTick()
      }
    }, 1000 / speed)
  }, [clearTick, updateState])

  const handleStart = useCallback((config: AARScenarioConfig) => {
    setScenarioConfig(config)
    configRef.current = config
    const state = initSimState(config)
    state.phase = 'RUNNING'
    state.isRunning = true
    updateState(state)
    startTick(state.speed)
  }, [updateState, startTick])

  const handlePause = useCallback(() => {
    if (!simState) return
    clearTick()
    updateState({ ...simState, phase: 'PAUSED', isRunning: false })
  }, [simState, clearTick, updateState])

  const handleResume = useCallback(() => {
    if (!simState) return
    const resumed = { ...simState, phase: 'RUNNING' as const, isRunning: true }
    updateState(resumed)
    startTick(resumed.speed)
  }, [simState, updateState, startTick])

  const handleStop = useCallback(() => {
    clearTick()
    setSimState(null)
    setScenarioConfig(null)
    configRef.current = null
    stateRef.current = null
  }, [clearTick])

  const handleSpeedChange = useCallback((speed: 1 | 2 | 5 | 10) => {
    if (!simState) return
    const updated = { ...simState, speed }
    updateState(updated)
    if (simState.phase === 'RUNNING') {
      startTick(speed)
    }
  }, [simState, updateState, startTick])

  const handleContingency = useCallback((contingency: AARContingency) => {
    if (!simState) return
    const withContingency = { ...simState, contingencies: [...simState.contingencies, contingency] }
    const result = applyContingency(withContingency, contingency)
    updateState(result)
    stateRef.current = result
    // Auto-pause on mission abort
    if (result.missionAborted && !simState.missionAborted) {
      clearTick()
      updateState({ ...result, phase: 'PAUSED', isRunning: false })
    }
  }, [simState, updateState, clearTick])

  // Time scrub handler — re-init from config and fast-forward
  const handleScrub = useCallback((targetTime: number) => {
    const config = configRef.current
    if (!config || !simState || simState.phase !== 'PAUSED') return

    let state = initSimState(config)
    state.phase = 'RUNNING'
    state.isRunning = true
    // Fast-forward by ticking to targetTime
    for (let i = 0; i < targetTime; i++) {
      state = tickSimulation(state)
      if (state.phase === 'COMPLETE') break
    }
    // Re-pause at target time
    state.phase = 'PAUSED'
    state.isRunning = false
    updateState(state)
  }, [simState, updateState])

  const startTimeZulu = scenarioConfig?.startTimeZulu ?? '0000'

  // SETUP phase — show ScenarioSetup
  if (!simState) {
    return (
      <div className="flex flex-col gap-4 flex-1 min-h-0">
        <div className="text-xs text-term-textDim uppercase tracking-wider font-bold">
          AAR Refueling Simulation
        </div>
        <ScenarioSetup onStart={handleStart} />
      </div>
    )
  }

  // RUNNING / PAUSED / COMPLETE — show simulation UI
  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0 overflow-hidden">
      {/* Mission Abort Banner */}
      {simState.missionAborted && (
        <div className="bg-red-500/20 border border-red-500/50 rounded px-4 py-2 flex items-center justify-center gap-3">
          <span className="text-red-400 font-bold text-sm uppercase tracking-wider animate-pulse">MISSION ABORT</span>
          <span className="text-[11px] text-red-300">Insufficient tanker capacity — simulation auto-paused</span>
        </div>
      )}

      {/* Header with Zulu time + relative time + controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="text-sm font-mono font-bold text-term-primary tabular-nums">
            {formatZuluTime(startTimeZulu, simState.currentTime)}
          </div>
          <div className="text-[10px] text-term-textDim font-mono tabular-nums">
            {formatSimTime(simState.currentTime)}
          </div>
          {scenarioConfig && (
            <div className="text-[10px] text-term-textDim">
              {scenarioConfig.name} &middot; {scenarioConfig.timeWindowMin} min window
            </div>
          )}
          {simState.phase === 'COMPLETE' && (
            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400">
              COMPLETE
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Speed controls */}
          {SPEED_OPTIONS.map(s => (
            <button
              key={s}
              onClick={() => handleSpeedChange(s as 1 | 2 | 5 | 10)}
              className={`px-2 py-1 text-[10px] font-bold rounded border transition-colors ${
                simState.speed === s
                  ? 'bg-term-primary/20 border-term-primary text-term-primary'
                  : 'bg-term-bg border-term-border text-term-textDim hover:border-term-primary/50'
              }`}
            >
              {s}x
            </button>
          ))}

          <div className="w-px h-5 bg-term-border mx-1" />

          {/* Playback controls */}
          {simState.phase === 'RUNNING' && (
            <button onClick={handlePause} className="term-button px-3 py-1 text-[10px] font-bold">
              PAUSE
            </button>
          )}
          {simState.phase === 'PAUSED' && (
            <button onClick={handleResume} className="term-button term-button-primary px-3 py-1 text-[10px] font-bold">
              RESUME
            </button>
          )}
          <button onClick={handleStop} className="px-3 py-1 text-[10px] font-bold rounded border border-red-500/30 text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors">
            RESET
          </button>
        </div>
      </div>

      {/* Gantt Chart (top) */}
      <GanttChart
        tankers={simState.tankers}
        receivers={simState.receivers}
        currentTime={simState.currentTime}
        totalTime={scenarioConfig?.timeWindowMin ?? 150}
        startTimeZulu={startTimeZulu}
        isPaused={simState.phase === 'PAUSED'}
        onScrub={handleScrub}
      />

      {/* Bottom section: Tanker Dashboard + Voice + Contingency */}
      <div className="flex gap-3 flex-1 min-h-0 overflow-hidden">
        {/* Left: Tanker Dashboard + Contingency */}
        <div className="w-1/2 flex flex-col gap-3 overflow-auto">
          <TankerDashboard
            tankers={simState.tankers}
            receivers={simState.receivers}
            currentTime={simState.currentTime}
          />
          <ContingencyPanel
            tankers={simState.tankers}
            receivers={simState.receivers}
            contingencies={simState.contingencies}
            onInjectContingency={handleContingency}
            disabled={simState.phase === 'COMPLETE'}
            missionAborted={simState.missionAborted}
          />
        </div>

        {/* Right: Voice Comms */}
        <div className="w-1/2 min-h-0">
          <VoiceScript voiceLog={simState.voiceLog} />
        </div>
      </div>
    </div>
  )
}
